import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ResolvedTheme, ThemePreference } from './theme';
import {
  applyResolvedTheme,
  getStoredThemePreference,
  resolveTheme,
  storeThemePreference
} from './theme';

type ThemeContextValue = {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (pref: ThemePreference) => void;
  toggle: () => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return ctx;
}

export const ThemeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => getStoredThemePreference());
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(getStoredThemePreference()));

  useEffect(() => {
    const nextResolved = resolveTheme(preference);
    setResolved(nextResolved);
    applyResolvedTheme(nextResolved);
    storeThemePreference(preference);
  }, [preference]);

  useEffect(() => {
    if (preference !== 'system') return;
    const mql = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mql) return;
    const mqlCompat = mql as MediaQueryList & {
      addListener?: (listener: (this: MediaQueryList, ev: MediaQueryListEvent) => any) => void;
      removeListener?: (listener: (this: MediaQueryList, ev: MediaQueryListEvent) => any) => void;
    };

    const onChange = () => {
      const nextResolved = resolveTheme('system');
      setResolved(nextResolved);
      applyResolvedTheme(nextResolved);
    };

    // Safari still needs addListener in some environments
    if (typeof mqlCompat.addEventListener === 'function') {
      mqlCompat.addEventListener('change', onChange);
    } else {
      mqlCompat.addListener?.(onChange as any);
    }
    return () => {
      if (typeof mqlCompat.removeEventListener === 'function') {
        mqlCompat.removeEventListener('change', onChange);
      } else {
        mqlCompat.removeListener?.(onChange as any);
      }
    };
  }, [preference]);

  const value = useMemo<ThemeContextValue>(() => {
    return {
      preference,
      resolved,
      setPreference: setPreferenceState,
      toggle: () => setPreferenceState(resolved === 'dark' ? 'light' : 'dark')
    };
  }, [preference, resolved]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

